// Updated RustDemo following latest prompt guidelines
export const UpdatedRustDemo = () => (
  <div className="font-mono text-sm space-y-2 text-left">
    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">1</span>
      <div className="flex-1">
        <span className="keyword hoverable"
              data-surface="struct User* user = malloc(sizeof(struct User)) → let user"
              data-concept="Rust's `let` bindings combine variable declaration with **ownership**. Unlike C's `malloc` which gives you a raw pointer, Rust allocates on the stack by default and tracks ownership at *compile time*."
              data-paradigm="Rust's **ownership system** ensures memory safety without garbage collection. When `user` goes out of scope, `Drop` is automatically called—eliminating the entire class of **use-after-free** and **double-free** bugs common in C."
              data-badge="Memory">let</span>
        <span className="text-gray-300"> </span>
        <span className="variable">user</span>
        <span className="text-gray-300"> = </span>
        <span className="type-keyword hoverable"
              data-surface="malloc(sizeof(struct User)) → User::new()"
              data-concept="Rust uses *associated functions* (like `::new()`) to construct types, ensuring **all fields are initialized**. C's `malloc` just allocates uninitialized bytes, leaving you responsible for setting every field."
              data-paradigm="Rust's type system enforces **complete initialization**. You can't access uninitialized memory like in C—the compiler guarantees every value is valid before use, preventing entire categories of *undefined behavior*."
              data-badge="Safety">User</span>
        <span className="text-gray-300">::</span>
        <span className="function hoverable"
              data-surface="{.name = &quot;Alice&quot;, .age = 30} → User::new(&quot;Alice&quot;, 30)"
              data-concept="*Constructor functions* encapsulate initialization logic. Instead of C's struct literals where you might forget a field, Rust methods ensure **complete, validated construction**."
              data-paradigm="Rust's constructor pattern enables **validation and invariant enforcement** at *creation time*. Unlike C where partial initialization is possible, Rust ensures objects are always in a **valid state** from the moment they're created."
              data-badge="Design">new</span>
        <span className="text-gray-300">(</span>
        <span className="string">&quot;Alice&quot;</span>
        <span className="text-gray-300">, </span>
        <span className="number">30</span>
        <span className="text-gray-300">);</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">2</span>
      <div className="flex-1">
        <span className="variable hoverable"
              data-surface="user-&gt;name = strdup(&quot;Bob&quot;) → user.name = String::from(&quot;Bob&quot;)"
              data-concept="Rust uses the same **dot notation** whether accessing owned data or references—the compiler *auto-dereferences*. C requires different syntax (`->` for pointers, `.` for values), making code harder to refactor."
              data-paradigm="Rust's *uniform access principle* hides the distinction between values and references at the syntax level. This reduces **cognitive load** and prevents the pointer-related bugs that plague C codebases."
              data-badge="Syntax">user</span>
        <span className="text-gray-300">.</span>
        <span className="variable">name</span>
        <span className="text-gray-300"> = </span>
        <span className="type-keyword hoverable"
              data-surface="strdup(&quot;Bob&quot;) → String::from(&quot;Bob&quot;)"
              data-concept="Rust's `String` type is a heap-allocated, growable **UTF-8 buffer** with known ownership. C's `strdup` allocates memory you must manually free, and there's no guarantee the string is valid UTF-8."
              data-paradigm="Rust's `String` type combines **memory safety** with **UTF-8 guarantees**. Unlike C strings which are just `char*` pointers with no length tracking, Rust's `String` prevents **buffer overflows** and encoding errors at *compile time*."
              data-badge="Types">String</span>
        <span className="text-gray-300">::</span>
        <span className="function">from</span>
        <span className="text-gray-300">(</span>
        <span className="string">&quot;Bob&quot;</span>
        <span className="text-gray-300">);</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">3</span>
      <div className="flex-1">
        <span className="text-gray-300"></span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">4</span>
      <div className="flex-1">
        <span className="keyword hoverable"
              data-surface="int* array = malloc(sizeof(int) * size) → let mut vec"
              data-concept="Rust's `mut` keyword makes mutability explicit and opt-in. C's pointers are mutable by default, making it harder to track where data changes. Rust forces you to declare intent upfront."
              data-paradigm="Explicit mutability is a cornerstone of Rust's safety model. By requiring `mut`, Rust makes data flow visible in the code, enabling better optimization and preventing accidental modifications that cause bugs in C."
              data-badge="Mutability">let</span>
        <span className="text-gray-300"> </span>
        <span className="keyword">mut</span>
        <span className="text-gray-300"> </span>
        <span className="variable">vec</span>
        <span className="text-gray-300"> = </span>
        <span className="function hoverable"
              data-surface="malloc(sizeof(int) * size) → vec![0; size]"
              data-concept="The `vec!` macro creates a fully initialized vector with known capacity and length. C's malloc gives you uninitialized memory—accessing it before initialization is undefined behavior."
              data-paradigm="Rust's collections track capacity, length, and ownership automatically. Unlike C arrays which are just pointers, Vec knows its own size and prevents out-of-bounds access at runtime, eliminating buffer overflows."
              data-badge="Collections">vec!</span>
        <span className="text-gray-300">[</span>
        <span className="number">0</span>
        <span className="text-gray-300">; </span>
        <span className="variable">size</span>
        <span className="text-gray-300">];</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">5</span>
      <div className="flex-1">
        <span className="variable">vec</span>
        <span className="text-gray-300">.</span>
        <span className="function hoverable"
              data-surface="array[count++] = value → vec.push(value)"
              data-concept="Rust's `push` method handles capacity growth automatically, reallocating when needed. In C, you manually track capacity and call realloc, risking buffer overflows if you miscalculate."
              data-paradigm="Rust's Vec abstracts away memory management complexity while maintaining performance. The type system ensures you can't push to an immutable vector, and the borrow checker prevents iterator invalidation—both common C pitfalls."
              data-badge="Methods">push</span>
        <span className="text-gray-300">(</span>
        <span className="number">42</span>
        <span className="text-gray-300">);</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">6</span>
      <div className="flex-1">
        <span className="text-gray-300"></span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">7</span>
      <div className="flex-1">
        <span className="keyword hoverable"
              data-surface="if (ptr != NULL) { use(ptr); } → if let Some(value) = option"
              data-concept="Rust's `if let` combines **pattern matching** with conditional execution. Instead of C's manual null checks that you can forget, Rust's `Option` type **forces you to handle** the `None` case to access the value."
              data-paradigm="The `Option` enum makes **nullability explicit** in the type system. In C, any pointer might be `NULL`, requiring defensive checks everywhere. Rust makes the possibility of absence visible in the type signature, eliminating Tony Hoare's *billion-dollar mistake*."
              data-badge="Patterns">if</span>
        <span className="text-gray-300"> </span>
        <span className="keyword">let</span>
        <span className="text-gray-300"> </span>
        <span className="type-keyword hoverable"
              data-surface="NULL check → Some(value)"
              data-concept="Pattern matching destructures the Option and binds the inner value in one step. In C, you check NULL then dereference separately—two operations where you can make mistakes."
              data-paradigm="Rust's pattern matching is exhaustive—the compiler ensures you handle all cases. Unlike C where forgetting a NULL check compiles fine but crashes at runtime, Rust won't compile until you handle None."
              data-badge="Enums">Some</span>
        <span className="text-gray-300">(</span>
        <span className="variable">value</span>
        <span className="text-gray-300">) = </span>
        <span className="variable">option</span>
        <span className="text-gray-300"> &#123;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">8</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;&nbsp;&nbsp;</span>
        <span className="function hoverable"
              data-surface="printf(&quot;Value: %d&quot;, value) → println!(&quot;Value: {}&quot;, value)"
              data-concept="Rust's `println!` macro is type-safe—it checks format strings at compile time. C's printf uses format specifiers like %d that can mismatch the argument type, causing undefined behavior."
              data-paradigm="Rust's macros enable compile-time validation of format strings. Unlike C where printf(&quot;%d&quot;, &quot;string&quot;) compiles but crashes, Rust catches these errors before runtime, and the `{}` syntax works for any type implementing Display."
              data-badge="Macros">println!</span>
        <span className="text-gray-300">(</span>
        <span className="string">&quot;Value: {}&quot;</span>
        <span className="text-gray-300">, </span>
        <span className="variable">value</span>
        <span className="text-gray-300">);</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">9</span>
      <div className="flex-1">
        <span className="text-gray-300">&#125;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">10</span>
      <div className="flex-1">
        <span className="text-gray-300"></span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">11</span>
      <div className="flex-1">
        <span className="comment hoverable"
              data-surface="free(user); free(array); → automatic cleanup"
              data-concept="Rust calls Drop automatically when values go out of scope. C requires manual free() calls for every malloc—forgetting one causes memory leaks, calling it twice causes crashes."
              data-paradigm="RAII (Resource Acquisition Is Initialization) ties resource lifetime to variable scope. Rust's ownership system guarantees cleanup happens exactly once at the right time, eliminating both leaks and double-frees that plague C programs."
              data-badge="RAII">// Memory is automatically freed when variables go out of scope</span>
      </div>
    </div>
  </div>
);

// Updated JavaScriptDemo following latest prompt guidelines
export const UpdatedJavaScriptDemo = () => (
  <div className="font-mono text-sm space-y-2 text-left">
    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">1</span>
      <div className="flex-1">
        <span className="keyword hoverable"
              data-surface="import asyncio → no import needed"
              data-concept="Python requires asyncio import vs JavaScript has built-in async/await support"
              data-paradigm="JavaScript eliminates Python's import complexity through native language features"
              data-badge="P">async</span>
        <span className="text-gray-300"> </span>
        <span className="keyword">function</span>
        <span className="text-gray-300"> </span>
        <span className="function hoverable"
              data-surface="async def fetch_data(): → async function fetchData()"
              data-concept="Python uses snake_case and def vs JavaScript uses camelCase and function keyword"
              data-paradigm="JavaScript eliminates Python's underscore convention through established camelCase patterns"
              data-badge="P">fetchData</span>
        <span className="text-gray-300">() &#123;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">2</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;</span>
        <span className="keyword">const</span>
        <span className="text-gray-300"> </span>
        <span className="variable">urls</span>
        <span className="text-gray-300"> = [</span>
        <span className="string">'/api/users'</span>
        <span className="text-gray-300">, </span>
        <span className="string">'/api/posts'</span>
        <span className="text-gray-300">];</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">3</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;</span>
        <span className="keyword">const</span>
        <span className="text-gray-300"> </span>
        <span className="variable">results</span>
        <span className="text-gray-300"> = </span>
        <span className="keyword hoverable"
              data-surface="await asyncio.gather(*tasks) → await Promise.all(promises)"
              data-concept="Python uses asyncio.gather for concurrent execution vs JavaScript uses Promise.all"
              data-paradigm="JavaScript eliminates Python's event loop complexity through Promise-based concurrency"
              data-badge="P">await</span>
        <span className="text-gray-300"> </span>
        <span className="type-keyword hoverable"
              data-surface="asyncio.gather(*[fetch(url) for url in urls]) → Promise.all(urls.map(...))"
              data-concept="Python unpacks list comprehension vs JavaScript uses Promise.all with map"
              data-paradigm="JavaScript eliminates Python's unpacking syntax through functional array methods"
              data-badge="P">Promise</span>
        <span className="text-gray-300">.</span>
        <span className="function">all</span>
        <span className="text-gray-300">(</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">4</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;&nbsp;&nbsp;</span>
        <span className="variable">urls</span>
        <span className="text-gray-300">.</span>
        <span className="function hoverable"
              data-surface="[await fetch(url) for url in urls] → urls.map(url => fetch(url))"
              data-concept="Python list comprehension vs JavaScript's map method for transformations"
              data-paradigm="JavaScript eliminates Python's special comprehension syntax through method chaining"
              data-badge="P">map</span>
        <span className="text-gray-300">(</span>
        <span className="variable">url</span>
        <span className="text-gray-300"> </span>
        <span className="operator hoverable"
              data-surface="lambda url: fetch(url) → url => fetch(url)"
              data-concept="Python lambda keyword vs JavaScript arrow function for inline functions"
              data-paradigm="JavaScript eliminates Python's lambda limitations through full arrow function syntax"
              data-badge="P">=&gt;</span>
        <span className="text-gray-300"> </span>
        <span className="function">fetch</span>
        <span className="text-gray-300">(</span>
        <span className="variable">url</span>
        <span className="text-gray-300">))</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">5</span>
      <div className="flex-1">
        <span className="text-gray-300">  );</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">6</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;</span>
        <span className="keyword">return</span>
        <span className="text-gray-300"> </span>
        <span className="variable">results</span>
        <span className="text-gray-300">;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">7</span>
      <div className="flex-1">
        <span className="text-gray-300">&#125;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">8</span>
      <div className="flex-1">
        <span className="text-gray-300"></span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">9</span>
      <div className="flex-1">
        <span className="keyword hoverable"
              data-surface="try: ... except Exception as e: → try { ... } catch (e)"
              data-concept="Python uses except keyword vs JavaScript uses catch for error handling"
              data-paradigm="JavaScript eliminates Python's except syntax through unified try-catch pattern"
              data-badge="P">try</span>
        <span className="text-gray-300"> &#123;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">10</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;</span>
        <span className="keyword">const</span>
        <span className="text-gray-300"> </span>
        <span className="variable">data</span>
        <span className="text-gray-300"> = </span>
        <span className="keyword">await</span>
        <span className="text-gray-300"> </span>
        <span className="function">fetchData</span>
        <span className="text-gray-300">();</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">11</span>
      <div className="flex-1">
        <span className="text-gray-300">&#125; </span>
        <span className="keyword hoverable"
              data-surface="except Exception as e: → catch (error)"
              data-concept="Python names exception with 'as' vs JavaScript directly names the parameter"
              data-paradigm="JavaScript eliminates Python's 'as' keyword through direct parameter naming"
              data-badge="P">catch</span>
        <span className="text-gray-300"> (</span>
        <span className="variable">error</span>
        <span className="text-gray-300">) &#123;</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">12</span>
      <div className="flex-1">
        <span className="text-gray-300">&nbsp;&nbsp;</span>
        <span className="variable">console</span>
        <span className="text-gray-300">.</span>
        <span className="function">error</span>
        <span className="text-gray-300">(</span>
        <span className="variable">error</span>
        <span className="text-gray-300">);</span>
      </div>
    </div>

    <div className="flex items-center">
      <span className="text-gray-500 w-8 text-right mr-4">13</span>
      <div className="flex-1">
        <span className="text-gray-300">&#125;</span>
      </div>
    </div>
  </div>
);